import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { api, call } from '../../../lib/api'
import { customersQuery, outletQuery, type OutletData, type Period } from '../../../lib/queries'
import { kg, nairaFull, nairaShort, num, orderDate } from '../../../lib/format'
import { BarChart, Ring } from '../../../components/charts'
import { OutletForm, TankForm } from '../../../components/outlet-forms'
import { SupplyPanel } from '../../../components/supply'
import { tdCls, thCls } from '../../../components/filters'
import { kgStat, Avatar, Button, Card, PageHeader, Cylinder, Empty, Icon, Modal, Pagination, PeriodSelect, StatCard, Tag, cx, levelWord, toast } from '../../../components/ui'

type Search = { tab?: 'retail'; period?: Period; tank?: string; page?: number }

export const Route = createFileRoute('/_app/outlets/$outletId')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    tab: s.tab === 'retail' ? 'retail' : undefined,
    period: ['today', 'week', 'quarter', 'year'].includes(s.period as string) ? (s.period as Period) : undefined,
    tank: (s.tank as string) || undefined,
    page: Number(s.page) > 1 ? Number(s.page) : undefined,
  }),
  loaderDeps: ({ search }) => ({ period: search.period ?? 'month' }),
  loader: ({ context, params, deps }) => context.queryClient.ensureQueryData(outletQuery(params.outletId, deps.period)),
  component: Outlet,
})

function Outlet() {
  const { outletId } = Route.useParams()
  const search = Route.useSearch()
  const period = search.period ?? 'month'
  const navigate = Route.useNavigate()
  const { data } = useSuspenseQuery(outletQuery(outletId, period))
  const [editing, setEditing] = useState(false)
  const tab = search.tab ?? 'supply'

  return (
    <div>
      <PageHeader
        title={data.outlet.name}
        subtitle={<><Link to="/outlets" className="hover:text-brand">Outlets</Link> · {data.outlet.region}, {data.outlet.state}</>}
        actions={<>
          <PeriodSelect value={period} onChange={(p) => navigate({ search: (s) => ({ ...s, period: p === 'month' ? undefined : p }), replace: true })} />
          <Button variant="light" onClick={() => setEditing(true)}>Edit outlet</Button>
        </>}
      />
      <div className="flex min-h-11 flex-wrap items-center gap-x-8 gap-y-2 rounded-lg bg-card p-2 text-sm">
        <span className="px-2 font-semibold text-brand">Outlets name:</span>
        <span className="font-semibold text-ink-3">{data.outlet.name}</span>
        <span className="flex items-center gap-1">
          <Icon name="star" className="mr-2 size-5" />
          {(['supply', 'retail'] as const).map((t, i) => (
            <span key={t} className="flex items-center gap-1">
              {i > 0 && <span className="text-faint">/</span>}
              <button onClick={() => navigate({ search: (s) => ({ ...s, tab: t === 'retail' ? 'retail' : undefined }), replace: true })} className={cx('rounded-lg px-2 py-1 capitalize', tab === t ? 'text-black' : 'text-ink/40 hover:text-ink')}>
                {t}
              </button>
            </span>
          ))}
        </span>
      </div>
      {tab === 'supply' ? <SupplyTab data={data} /> : <RetailTab data={data} />}
      <Modal open={editing} onClose={() => setEditing(false)} title="Edit outlet">
        <OutletForm initial={data.outlet} onDone={() => setEditing(false)} />
      </Modal>
    </div>
  )
}

type Data = OutletData

function SupplyTab({ data }: { data: Data }) {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const qc = useQueryClient()
  const s = data.supply
  const tank = data.tanks.find((t) => t.id === search.tank) ?? data.tanks[0]
  const [tankModal, setTankModal] = useState<'new' | 'edit' | null>(null)
  const [trigger, setTrigger] = useState<string | null>(null)
  const refresh = () => ['outlet', 'outlets', 'notifications'].forEach((k) => qc.invalidateQueries({ queryKey: [k] }))

  const supply = useMutation({
    mutationFn: (n: number) => call(api.outlets.tanks[':tankId'].supply.$post({ param: { tankId: tank!.id }, json: { kg: n } })),
    onSuccess: (r) => (toast(`${num(r.kg)}kg delivered to ${tank!.name}`), refresh()),
    onError: (e) => toast(e.message, 'error'),
  })
  const saveTrigger = useMutation({
    mutationFn: (n: number) => call(api.outlets.tanks[':tankId'].$patch({ param: { tankId: tank!.id }, json: { triggerKg: n } })),
    onSuccess: () => (toast('Trigger level saved'), setTrigger(null), refresh()),
    onError: (e) => toast(e.message, 'error'),
  })
  const low = tank && tank.levelKg <= tank.triggerKg

  return (
    <>
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-7">
        <StatCard tone="peach" label="Volume distributed" value={kgStat(s.distributedKg)} change={s.distributedChange} />
        <StatCard label="Supply counts" value={s.count} change={s.countChange} />
        <StatCard label="Amount supplied" value={nairaShort(s.amountKobo)} sub={nairaFull(s.amountKobo)} change={s.amountChange} />
        <StatCard tone="peach" label="Volume supplied" value={kgStat(s.suppliedKg)} change={s.suppliedChange} />
      </div>

      <h2 className="mt-10 text-sm font-semibold">Capacity Level</h2>
      <div className="mt-5 flex flex-wrap items-center gap-4">
        {data.tanks.map((t) => (
          <button key={t.id} onClick={() => navigate({ search: (x) => ({ ...x, tank: t.id }), replace: true })} className={cx('h-[26px] px-2.5', t.id === tank?.id ? 'bg-peach text-[13px] font-semibold text-black' : 'text-xs text-ink-3 hover:text-brand')}>
            {t.name}
            {t.levelKg <= t.triggerKg && <span className="ml-1 inline-block size-1.5 rounded-full bg-danger align-middle" />}
          </button>
        ))}
        <button onClick={() => setTankModal('new')} className="text-xs font-semibold text-brand hover:underline">+ Add tank</button>
      </div>

      {!tank ? (
        <Card className="mt-6"><Empty title="No tanks yet">Add a tank to track this outlet's gas and supply it.</Empty></Card>
      ) : (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className="border border-line bg-white/40 p-3 shadow-card">
            <div className="flex flex-col-reverse items-center gap-6 sm:flex-row sm:items-start sm:justify-between">
              <ul className="w-full space-y-4 pt-5 sm:w-[178px]">
                {[
                  ['tank', 'bg-brand', 'Capacity:', kg(tank.capacityKg)],
                  ['thermometer', 'bg-good', 'Temperature:', tank.temperatureC != null ? `${tank.temperatureC}°` : '—'],
                  ['truck', 'bg-brand', 'Volume supplied:', kg(tank.suppliedKg)],
                ].map(([img, bg, label, value]) => (
                  <li key={label} className="flex h-12 overflow-hidden rounded-r-[10px] bg-peach">
                    <span className={cx('grid w-[50px] flex-none place-items-center', bg)}><img src={`/img/${img}.webp`} alt="" width={34} height={34} /></span>
                    <span className="flex flex-col justify-center px-2.5 leading-[18px]">
                      <span className="text-[11px] font-medium">{label}</span>
                      <span className="num text-base font-extrabold">{value}{img === 'thermometer' && tank.temperatureC != null && <i className="text-[13px] font-medium">(celsius)</i>}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col items-center gap-3 pt-5">
                <Ring value={tank.levelKg} max={tank.capacityKg} color={low ? '#f86822' : '#2cc76c'}>
                  <div>
                    <p className="text-xs font-semibold text-[#637381]">Total volume left</p>
                    <p className="num text-2xl leading-[48px] font-bold text-[#212b36]">{num(tank.levelKg)}kg</p>
                  </div>
                </Ring>
                <span className="flex items-center gap-2 text-xs text-[#212b36]"><span className={cx('size-3 rounded-full', low ? 'bg-brand' : 'bg-good-2')} />{low ? 'Below trigger level' : 'Gas Volume'}</span>
              </div>
            </div>
            <form
              className="mt-10"
              onSubmit={(e) => {
                e.preventDefault()
                if (trigger != null && Number(trigger) >= 0) saveTrigger.mutate(Number(trigger))
              }}
            >
              <label className="text-xs text-[#999]" htmlFor="trigger">Set trigger volume level</label>
              <div className="mt-1 flex flex-wrap items-center gap-6">
                <span className="flex h-[34px] w-[133px] items-center rounded-lg border border-[#f9f6f8] bg-white px-3 shadow-[0_7px_13px_rgb(68_74_221/0.08)]">
                  <input id="trigger" inputMode="decimal" value={trigger ?? String(tank.triggerKg)} onChange={(e) => setTrigger(e.target.value.replace(/[^\d.]/g, ''))} className="num w-full bg-transparent text-center text-base font-semibold text-brand focus:outline-none" />
                  <span className="text-base font-semibold text-brand">kg</span>
                </span>
                <button disabled={trigger == null || saveTrigger.isPending} className="flex h-[35px] w-[120px] items-center justify-center gap-1 rounded-lg border border-peach bg-brand text-sm font-bold text-white disabled:opacity-60">
                  Enter <Icon name="arrow-enter" className="size-3.5" />
                </button>
                <button type="button" onClick={() => setTankModal('edit')} className="text-xs text-muted hover:text-ink">Edit tank</button>
              </div>
              {tank.devEui && <p className="mt-3 text-[11px] text-muted">Meter <span className="font-mono">{tank.devEui}</span>{tank.lastSeenAt ? ` · last reading ${orderDate(tank.lastSeenAt)}` : ''}</p>}
            </form>
          </div>
          <SupplyPanel
            title="Supply this outlet"
            balanceKg={data.depotBalanceKg}
            pricePerKg={data.wholesalePerKg}
            maxKg={Math.max(0, Math.round(tank.capacityKg - tank.levelKg))}
            busy={supply.isPending}
            onSupply={(n) => supply.mutateAsync(n)}
          />
        </div>
      )}
      <Modal open={!!tankModal} onClose={() => setTankModal(null)} title={tankModal === 'edit' ? `Edit ${tank?.name}` : 'Add tank'}>
        <TankForm outletId={data.outlet.id} initial={tankModal === 'edit' ? tank : undefined} onDone={() => setTankModal(null)} />
      </Modal>
    </>
  )
}

function RetailTab({ data }: { data: Data }) {
  const r = data.retail
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { data: list } = useQuery(customersQuery({ outletId: data.outlet.id, page: search.page, sort: 'lastOrder' }))
  const month = new Intl.DateTimeFormat('en', { month: 'short' })

  return (
    <>
      <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_432px] lg:gap-7">
        <div className="grid grid-cols-2 gap-4 lg:gap-7">
          <StatCard tone="peach" label="Customers" value={num(r.customers)} change={r.customersChange} />
          <StatCard label="Orders" value={num(r.orders)} change={r.ordersChange} />
          <StatCard label="Revenue" value={nairaShort(r.revenueKobo)} sub={nairaFull(r.revenueKobo)} change={r.revenueChange} />
          <StatCard tone="peach" label="Growth" value={`${r.growth.toFixed(1)}%`} change={r.growth} />
        </div>
        <Card className="p-6">
          <h2 className="mb-2 text-sm font-semibold">Projections vs Actuals</h2>
          <BarChart
            height={180}
            format={nairaShort}
            data={r.projections.map((p) => ({ label: month.format(new Date(p.month + '-15')), value: p.actualKobo, color: '#f86822', cap: p.projectedKobo, capColor: '#fec1a4' }))}
          />
          <p className="mt-1 flex gap-4 text-[11px] text-muted"><span><span className="mr-1 inline-block size-2 rounded-sm bg-brand" />Actual</span><span><span className="mr-1 inline-block size-2 rounded-sm bg-peach-soft" />Projection (3-month trend)</span></p>
        </Card>
      </div>

      <div className="mt-10 mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Customers List</h2>
        <Link to="/customers" search={{ outletId: data.outlet.id }} className="text-xs font-semibold text-brand hover:underline">Open in Customers →</Link>
      </div>
      <div className="overflow-x-auto bg-white">
        <table className="w-full min-w-[760px]">
          <thead><tr><th className={thCls}>User</th><th className={thCls}>Estimated Tank Storage</th><th className={thCls}>Address</th><th className={thCls}>Last Order</th><th className={thCls}>Gas level</th></tr></thead>
          <tbody>
            {list?.customers.map((c) => (
              <tr key={c.id} className="border-b border-black/5 last:border-0 hover:bg-page/70">
                <td className={tdCls}><Link to="/customers/$customerId" params={{ customerId: c.id }} className="flex items-center gap-2 hover:text-brand"><Avatar name={c.name} photoKey={c.photoKey} />{c.name}{c.isRetailAgent && <Tag>Retail Agent</Tag>}</Link></td>
                <td className={tdCls}>{kg(c.cylinderKg)}</td>
                <td className={cx(tdCls, 'max-w-[260px] truncate')}>{c.address}</td>
                <td className={tdCls}><span className="flex items-center gap-1.5"><Icon name="calendar" className="size-4" />{orderDate(c.lastOrderAt)}</span></td>
                <td className={tdCls}><span className="flex items-center gap-2"><Cylinder level={c.level} />{levelWord(c.level)}, {c.levelKg}/{kg(c.cylinderKg)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {list && !list.customers.length && <Empty title="No customers at this outlet yet" />}
      </div>
      {list && <Pagination page={list.page} total={list.total} pageSize={list.pageSize} onPage={(page) => navigate({ search: (s) => ({ ...s, page: page > 1 ? page : undefined }) })} />}
    </>
  )
}
